import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import type { DbNewsArticle } from "@/types/database";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import Icon from "@/components/feature/Icon";

const categoryColors: Record<string, string> = {
  出版品: "bg-emerald-50 text-emerald-700 border-emerald-100",
  合作公告: "bg-navy-50 text-navy-700 border-navy-100",
  活動公告: "bg-amber-50 text-amber-700 border-amber-100",
  國際交流: "bg-sky-50 text-sky-700 border-sky-100",
  政策建言: "bg-rose-50 text-rose-700 border-rose-100",
  協會公告: "bg-violet-50 text-violet-700 border-violet-100",
  產業觀察: "bg-teal-50 text-teal-700 border-teal-100",
};

function NewsRowLink({
  news,
  className,
  style,
  children,
}: {
  news: DbNewsArticle;
  className: string;
  style: React.CSSProperties;
  children: React.ReactNode;
}) {
  if (news.link_url) {
    return (
      <a href={news.link_url} target="_blank" rel="noopener noreferrer" className={className} style={style}>
        {children}
      </a>
    );
  }
  return (
    <Link to="/events" className={className} style={style}>
      {children}
    </Link>
  );
}

export default function NewsSection() {
  const [newsItems, setNewsItems] = useState<DbNewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const { ref: headerRef, style: headerStyle } = useScrollReveal();

  useEffect(() => {
    async function fetchNews() {
      const { data, error } = await supabase
        .from("news_articles")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true })
        .order("id", { ascending: true })
        .limit(6);
      if (!error && data) {
        setNewsItems(data as DbNewsArticle[]);
      }
      setLoading(false);
    }
    fetchNews();
  }, []);

  return (
    <section className="py-24 md:py-32 bg-white">
      <div className="px-6 md:px-10 lg:px-16">
        {/* Section header */}
        <div ref={headerRef} style={headerStyle} className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12 md:mb-16">
          <div>
            <p className="text-sm font-medium tracking-[0.2em] text-navy-700 uppercase mb-3">
              Latest News
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-950">
              最新消息
            </h2>
          </div>
          <Link
            to="/events"
            className="text-sm font-medium text-navy-950 hover:text-navy-700 transition-colors inline-flex items-center gap-1 whitespace-nowrap group"
          >
            查看全部
            <Icon className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <Icon className="ri-loader-4-line animate-spin text-2xl text-silver-400 inline-block mb-2" />
            <p className="text-sm text-silver-400">載入中...</p>
          </div>
        ) : (
          <div className="border border-silver-200 rounded-2xl overflow-hidden divide-y divide-silver-100">
            {newsItems.map((news, index) => {
              const parts = news.date.split(".");
              const year = parts[0] || "";
              const monthDay = parts[1] || "";
              const month = monthDay.slice(0, 2);
              const day = monthDay.slice(2);
              return (
                <NewsRowLink
                  key={news.id}
                  news={news}
                  className="group flex items-start gap-5 md:gap-8 p-5 md:p-6 hover:bg-silver-50/60 transition-colors duration-300"
                  style={{ animationDelay: `${index * 80}ms` }}
                >
                  {/* Date block */}
                  <div className="flex-shrink-0 w-14 md:w-16 text-center">
                    <p className="text-[10px] md:text-xs font-medium text-silver-400 uppercase tracking-wider">{year}</p>
                    <p className="text-lg md:text-xl font-bold text-navy-950 font-serif leading-tight">{month}</p>
                    <p className="text-[10px] md:text-xs text-silver-400">{day}</p>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className={`inline-block text-xs font-medium px-2.5 py-0.5 rounded border ${categoryColors[news.category] || "bg-silver-50 text-silver-600 border-silver-100"}`}>
                        {news.category}
                      </span>
                    </div>
                    <h3 className="text-base md:text-lg font-semibold text-navy-950 group-hover:text-navy-700 transition-colors mb-1.5 leading-snug">
                      {news.title}
                    </h3>
                    <p className="text-sm text-silver-500 leading-relaxed line-clamp-2">
                      {news.summary || ""}
                    </p>
                  </div>

                  {/* Arrow */}
                  <div className="hidden md:flex flex-shrink-0 w-10 h-10 rounded-full border border-silver-200 items-center justify-center group-hover:border-navy-950 group-hover:bg-navy-950 transition-all duration-300">
                    <Icon className="ri-arrow-right-line text-silver-400 group-hover:text-white transition-colors" />
                  </div>
                </NewsRowLink>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}